import React from 'react';
import { Search } from 'lucide-react';
import { SimpleInput, SegmentedFilter, type SegmentedFilterOption } from '@/components/ui';
import {
  CARGO_SEGMENTS,
  CARGO_SEGMENT_FILTER_ALL,
  CARGO_SEGMENT_LABELS,
  type CargoSegmentFilter,
} from '@/constants/cargoList.constants';
import type { CargoListFilters } from '@/types/cargoList.types';

/**
 * All | VLCC | Suezmax. Built from the segment map so the labels cannot drift
 * from the codes the filter actually compares against.
 */
const SEGMENT_OPTIONS: readonly SegmentedFilterOption<CargoSegmentFilter>[] = [
  { value: CARGO_SEGMENT_FILTER_ALL, label: 'All' },
  ...CARGO_SEGMENTS.map((segment) => ({
    value: segment,
    label: CARGO_SEGMENT_LABELS[segment],
  })),
];

interface CargoListFiltersProps {
  filters: CargoListFilters;
  onFiltersChange: (filters: Partial<CargoListFilters>) => void;
  loading?: boolean;
}

export function CargoListFiltersComponent({
  filters,
  onFiltersChange,
  loading = false,
}: CargoListFiltersProps) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      {/* Search */}
      <div className="min-w-0 flex-1">
        <SimpleInput
          type="text"
          placeholder="Search charterer, trade, or status..."
          value={filters.searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onFiltersChange({ searchQuery: e.target.value })
          }
          disabled={loading}
          leftIcon={<Search className="h-5 w-5" />}
        />
      </div>

      {/* Vessel type, from the view's `Segment` column. Named for the vessel
          class rather than the tonnage, so it does not read as a filter on the
          "Cargo Size" column showing MT. Filters the rows already fetched - the
          list endpoint takes no parameters and returns every cargo. */}
      <SegmentedFilter
        options={SEGMENT_OPTIONS}
        value={filters.segmentFilter}
        onChange={(segmentFilter) => onFiltersChange({ segmentFilter })}
        ariaLabel="Vessel Type"
        className="sm:w-auto sm:flex-shrink-0"
      />
    </div>
  );
}
